import { DEFAULT_EXCLUDE_SENTRY_STATUS_CODES } from "@gettactic/api";
import { apiErrorBodies } from "./apiErrorBodies";
import { getApiErrorCode } from "./getApiErrorCode";
import { getApiErrorStatus } from "./getApiErrorStatus";
import { UserFacingError } from "./UserFacingError";

/**
 * The parts of a Sentry event this helper touches. Kept structural so the
 * helper works with both `@sentry/nextjs` and `@sentry/react-native` events
 * without depending on either.
 */
export type SentryEventLike = {
  fingerprint?: string[];
  tags?: Record<string, unknown>;
  exception?: { values?: { type?: string; value?: string }[] };
};

// Messages browsers and React Native use when a request never reached the
// server: the user is offline or navigated away. Nothing for us to fix.
const NETWORK_ERROR_MESSAGES: readonly string[] = [
  "Failed to fetch",
  "Load failed",
  "NetworkError when attempting to fetch resource",
  "Network request failed",
  "The network connection was lost",
  "The Internet connection appears to be offline"
];

/**
 * Whether an error never reached the server (offline, aborted request).
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === "AbortError") {
    return true;
  }

  return NETWORK_ERROR_MESSAGES.some((message) =>
    error.message.startsWith(message)
  );
}

/**
 * Whether the API turned a reservation down because the resource or the user
 * isn't available then (taken, concurrent booking, outside the booking
 * window...). The answer is a 400 shown to the user, not a bad request.
 */
export function isAvailabilityRejection(error: unknown): boolean {
  return apiErrorBodies(error).some(
    (body) =>
      Array.isArray(body.availability_errors) &&
      body.availability_errors.length > 0
  );
}

// Business rules the API answers on purpose, and the user is told about:
// Outlook/Teams meetings can't be removed from a reservation, and ticket-only
// resources can only be booked through a workplace request.
const EXPECTED_API_ERROR_CODES: readonly string[] = [
  "online_meeting_cannot_delete",
  "resource_ticket_only"
];

/**
 * Whether an error is worth an issue in Sentry: something we can fix in code.
 * Network failures, errors already shown to the user and expected API answers
 * (401, 404, 409, 422, availability rejections, business rules...) are not.
 */
export function shouldReportError(error: unknown): boolean {
  if (isNetworkError(error) || error instanceof UserFacingError) {
    return false;
  }

  if (isAvailabilityRejection(error)) {
    return false;
  }

  const code = getApiErrorCode(error);
  if (code && EXPECTED_API_ERROR_CODES.includes(code)) {
    return false;
  }

  const status = getApiErrorStatus(error);
  if (status === undefined) {
    return true;
  }

  return !DEFAULT_EXCLUDE_SENTRY_STATUS_CODES.includes(status);
}

/**
 * Use as Sentry's `beforeSend`: drops errors that aren't worth an issue, and
 * groups API failures by endpoint, status and error code instead of lumping
 * every failed request into one "No error message" issue.
 */
export function prepareSentryEvent<E extends SentryEventLike>(
  event: E,
  error: unknown
): E | null {
  if (!shouldReportError(error)) {
    return null;
  }

  const status = getApiErrorStatus(error);
  if (status === undefined || !error || typeof error !== "object") {
    return event;
  }

  const { method, endpoint } = error as {
    method?: unknown;
    endpoint?: unknown;
  };
  if (typeof method !== "string" || typeof endpoint !== "string") {
    return event;
  }

  // One endpoint answers 400 for many reasons (a wrong invite code, a missing
  // field...); the code tells them apart so each can be fixed or ignored.
  const code = getApiErrorCode(error);

  event.fingerprint = ["api-client-error", method, endpoint, String(status)];
  if (code) {
    event.fingerprint.push(code);
  }
  event.tags = {
    ...event.tags,
    "api.method": method,
    "api.endpoint": endpoint,
    "api.status": status,
    ...(code ? { "api.code": code } : {})
  };

  const exception = event.exception?.values?.at(-1);
  if (exception) {
    exception.type = "ApiClientError";
    exception.value = `${method} ${endpoint} responded ${status}${code ? ` (${code})` : ""}`;
  }

  return event;
}
